import { forwardRef, useImperativeHandle, useRef } from 'react';
import './style.scss';
import type { IMunicipality } from '../../../models/municipality.model';
import { ClientSideStorageItems } from '../../../services/client-side-storage.service';
import MunicipalityAutocomplete from '../MunicipalityAutocomplete';
import SelectedFilterValue from '../SelectedFilterValue';
import { type IAutocomplete } from '../AutocompleteBase';
import ValidationInfo from '../../../validation/validation-info';
import type { IAutocompleteItem } from '../../../models/autocomplete-item.model';
import { MunicipalitySelectorLayout } from './municipality-selector-layout.enum';
import { AutocompleteLabelPosition } from '../AutocompleteBase/autocomplete-base.enum';

// === INTERFACES AND ENUMS ===
export interface IMunicipalitySelector {
  clear: () => void;
}

interface IMunicipalitySelectorProps {
  id: string;
  placeholderText: string;
  noResultsText: string;
  selectedMunicipalities: IMunicipality[];
  layout?: MunicipalitySelectorLayout;
  commonSearches?: IAutocompleteItem[];
  onChange: (selectedMunicipalities: IMunicipality[]) => void;
}

export default forwardRef<IMunicipalitySelector, IMunicipalitySelectorProps>(function MunicipalitySelector(
  { id, placeholderText, noResultsText, selectedMunicipalities, layout = MunicipalitySelectorLayout.vertical, commonSearches, onChange }: IMunicipalitySelectorProps,
  ref
) {
  const municipalityAutocompleteRef = useRef<IAutocomplete>(null);

  useImperativeHandle(ref, () => ({
    clear() {
      municipalityAutocompleteRef.current?.clear();
    },
  }));

  function handleMunicipalityAutocompleteSelect(selectedMunicipality: IMunicipality | undefined) {
    if (!selectedMunicipality) {
      return;
    }
    if (selectedMunicipalities.some((m) => m.gid === selectedMunicipality.gid)) {
      // The municipality is already selected
      municipalityAutocompleteRef.current?.clear();
    } else {
      // New municipality selected
      municipalityAutocompleteRef.current?.addSelectedItemToHistory(selectedMunicipality);
      municipalityAutocompleteRef.current?.clear();
      onChange([...selectedMunicipalities, selectedMunicipality]);
    }
  }

  function handleSelectedMunicipalityRemove(gid: string) {
    onChange(selectedMunicipalities.filter((m) => m.gid !== gid));
  }

  return (
    <div className={`municipality-selector mb-n2 ${layout === MunicipalitySelectorLayout.horizontal ? 'municipality-selector--layout-horizontal' : ''}`}>
      <div className="municipality-selector__autocomplete-wrapper mb-2">
        <MunicipalityAutocomplete
          id={id}
          ref={municipalityAutocompleteRef}
          labelPosition={AutocompleteLabelPosition.hide}
          placeholderText={placeholderText}
          noResultsText={noResultsText}
          historyProps={{ localStorageItem: ClientSideStorageItems.LocalStorage.MunicipalityAutocompleteHistory, commonSearches }}
          state={{
            value: undefined,
            validationInfo: new ValidationInfo('', []),
          }}
          onSelect={handleMunicipalityAutocompleteSelect}
        />
      </div>

      {selectedMunicipalities.length > 0 && (
        <div className="municipality-selector__selected-municipalities d-flex flex-row flex-wrap">
          {selectedMunicipalities.map((selectedMunicipality) => (
            <SelectedFilterValue
              id={selectedMunicipality.gid}
              key={selectedMunicipality.gid}
              ariaLabel={selectedMunicipality.name}
              className="mb-2 me-2"
              children={selectedMunicipality.name}
              onRemove={() => handleSelectedMunicipalityRemove(selectedMunicipality.gid)}
            />
          ))}
        </div>
      )}
    </div>
  );
});
